import React from "react";
import * as Mui from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  CalendarMonthOutlined,
  CheckRounded,
  CurrencyRupeeOutlined,
  ErrorOutlineRounded,
  InfoOutlined,
  PlaceOutlined,
  RemoveRounded,
  ScheduleOutlined,
} from "@mui/icons-material";
import { Loader } from "components/Loader";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { MilestoneStepper, RecordHeaderCard, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { money } from "./lwfEmployeesData";

// Employee LWF Details (LWF2a covered / LWF2b not covered), from GET
// employees/<employee_id>/: the site and rule, each coverage check with a
// tick or the rule that excludes, the next deduction and history. Read
// only: coverage changes only through the employee's category / work
// location or the site's rules.

const { indigo, green, orange, pink, blue, purple } = detailAccentPalette;
const STATUS_COLORS = { green: "#12B76A", slate: "#6B7280", red: "#DC2626" };
export const EMPLOYEE_PROFILE_ROUTE = "/admin/hrzone/employees";
export const CONFIG_ROUTE = "/admin/payroll/configuration/statutory-configuration?item=lwf";

const REASON_TITLES = { covered: "WHY COVERED", not: "WHY NOT COVERED", held: "WHY HELD", notsetup: "WHY NO LWF" };

// if_this_changes may be a sentence or { label, detail }.
const changeLine = (value) => {
  if (!value) return null;
  const text = typeof value === "object" ? value.detail || value.text || value.label : value;
  return text ? { icon: InfoOutlined, accent: blue, label: (typeof value === "object" && value.label) || "If this changes", value: String(text), multiline: true } : null;
};

export const LwfEmployeePanel = ({ open, loading, error, detail, onRetry, onClose }) => {
  const history = useHistory();
  const row = detail?.row;
  const key = row?.status_key;

  const footer = (
    <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
      {detail?.open_profile ? <SecondaryButton onClick={() => history.push(EMPLOYEE_PROFILE_ROUTE)}>Open employee profile</SecondaryButton> : null}
      {key === "notsetup" ? <SecondaryButton onClick={() => history.push(CONFIG_ROUTE)}>Set up site</SecondaryButton> : null}
      <PrimaryButton onClick={onClose}>Close</PrimaryButton>
    </Mui.Stack>
  );

  const body = () => {
    if (loading) {
      return (
        <Mui.Box role="status" aria-label="Loading employee LWF details">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      );
    }
    if (error) {
      return (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load the employee's LWF details."
          body={error}
          action={
            <SecondaryButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>
              Retry
            </SecondaryButton>
          }
        />
      );
    }
    if (!row) return null;

    const site = detail.site || {};
    const siteName = site.name || row.site_name;
    const meta = [row.emp_id, row.department, siteName, row.category || "no category"].filter(Boolean).join(" · ");

    const siteFields = [
      {
        icon: PlaceOutlined,
        label: "Site",
        value: [siteName, site.state].filter(Boolean).join(" · ") || "—",
        helper: site.note || "From work location on the last day of the month",
        accent: purple,
      },
      ...(detail.how_deducted ? [{ icon: CalendarMonthOutlined, label: "How LWF is deducted", value: detail.how_deducted, accent: indigo }] : []),
    ];

    const reasonFields = [
      ...detail.why.map((check) => ({
        icon: check.ok ? CheckRounded : key === "held" || key === "notsetup" ? ErrorOutlineRounded : RemoveRounded,
        accent: check.ok ? green : key === "held" || key === "notsetup" ? pink : orange,
        label: check.label,
        value: check.detail || (check.ok ? "Yes" : "No"),
        multiline: true,
      })),
      changeLine(detail.if_this_changes),
    ].filter(Boolean);
    if (!reasonFields.length && key === "notsetup") {
      reasonFields.push(
        { icon: ErrorOutlineRounded, accent: pink, label: "Site", value: `${siteName} has no LWF set-up and is not marked Not applicable`, multiline: true },
        { icon: InfoOutlined, accent: blue, label: "How to fix", value: "Set up the site in Configuration › Statutory › Labour Welfare Fund, or mark it Not applicable", multiline: true },
      );
    }

    const next = detail.next_deduction;
    const nextFields =
      next && next.employee != null
        ? [
            { icon: ScheduleOutlined, label: "Month", value: next.label || row.next_label, accent: orange },
            { icon: CurrencyRupeeOutlined, label: "Employee pays", value: [money(next.employee), next.employee_note].filter(Boolean).join(" · "), accent: pink },
            { icon: CurrencyRupeeOutlined, label: "Company pays", value: [money(next.employer), next.employer_note].filter(Boolean).join(" · "), accent: purple },
          ]
        : [
            {
              icon: ScheduleOutlined,
              label: next?.label || (row.next_label !== "—" ? row.next_label : "Next deduction"),
              value: next?.text || (key === "held" ? "Held until the missing data is fixed" : "No LWF"),
              accent: blue,
            },
          ];

    const steps = detail.history.map((entry, index) => ({
      label: entry.label || entry.period || `#${index + 1}`,
      date: entry.text || entry.status_label || "",
      done: String(entry.status || "").toUpperCase() !== "NEXT",
    }));

    return (
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(row.name)}
          title={row.name}
          chip={<RequestStatusChip status={row.status_label} colorCode={STATUS_COLORS[row.status_tone]} />}
          meta={meta}
          tone={key === "covered" ? "teal" : key === "not" ? "slate" : "danger"}
        />
        {detail.estimate ? (
          <RequestInfoBanner body="Estimate — payroll for the deduction month has not run yet, so amounts come from the salary structure." />
        ) : null}
        <RequestDetailsSection title="LWF SITE" fields={siteFields} />
        {reasonFields.length ? <RequestDetailsSection title={REASON_TITLES[key] || "COVERAGE"} fields={reasonFields} /> : null}
        <RequestDetailsSection title="NEXT DEDUCTION" fields={nextFields} />
        {steps.length ? <MilestoneStepper title="History" steps={steps} /> : null}
        {key === "held" ? (
          <RequestInfoBanner tone="warning" body="Payroll still finalises; LWF for this employee is listed as Held in the register and in Validation until the data is fixed." />
        ) : null}
      </Mui.Stack>
    );
  };

  return (
    <GlobalSidePanel open={open} onClose={onClose} title="Employee LWF Details" subtitle="Labour Welfare Fund" bodySx={panelBodySx} footer={footer}>
      {body()}
    </GlobalSidePanel>
  );
};
